import { EventNames, globalEventBus } from '@littlebird/event-bus';
import {
  ChatStatus,
  type Message as TMessage,
  useStores,
} from '@littlebird/sdk';
import { captureException } from '@sentry/react';
import { useMatchRoute } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import React, {
  type ComponentRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import { VoteMessageModal } from '@/components/modals/VoteMessageModal';
import { XStack, YStack } from '@/components/ui';
import { loaderPortalId } from '@/components/ui/logo-loader';
import {
  createBatchAwareSpeed,
  Markdown,
  Smoothdown,
} from '@/components/ui/smoothdown';
import { MessageActionCards } from '@/features/action-cards/components/MessageActionCards';
import {
  AskUserAnswersSummary,
  AskUserPicker,
  findAskUserCard,
} from '@/features/ask-user';
import { usePendingSandboxFiles } from '@/features/chat/chat.hooks';
import { useAnimationSlot } from '@/features/chat/chat.store';
import { canUseSpeechFeatures } from '@/features/chat/voice/speech-features';
import {
  HivemindIncomingMessage,
  hivemindIncomingSenderOf,
} from '@/features/hivemind';
import { useAppMentionLinks } from '@/features/mention/use-app-mention-links';
import { usePersonMentionLinks } from '@/features/people-wiki/mentions';
import { useTruncate } from '@/hooks/useTruncate';
import { afterFrames } from '@/lib/animate';
import { cn } from '@/lib/utils';
import {
  useVote,
  VoteDataDirection,
  type VoteDataDirectionType,
} from '@/query/vote';
import { inElectron } from '@/utils';
import { submitSurvey, trackEvent } from '@/utils/analytics';
import { logChatRenderDebug } from '@/utils/chat-render-debug';
import { classList } from '@/utils/cn';

import { IconButton } from '../../ui/IconButton';
import CopyButton from '../CopyButton';

import { Attachments, PendingAssistantFiles } from './Attachments';
import { CopyAsImageButton } from './CopyAsImageButton';
import { MessageQuotes } from './MessageQuotes';
import { MessageSources } from './messageSources/MessageSources';
import { MessageVersionSwitcher } from './MessageVersionSwitcher';
import { SpeakResponseButton } from './SpeakResponseButton';
import { useAssistantMessageCallbacks } from './useAssistantMessageCallbacks';
import { UserMessageEditor } from './UserMessageEditor';
import { useUserMessageEdit } from './useUserMessageEdit';

interface MessageProps {
  threadId: string;
  messageIndex: number;
  message: TMessage;
  isCompact?: boolean;
  allowEdit?: boolean;
  isLastAssistantMessage?: boolean;
  isNewestResponse?: boolean;
  isLive?: boolean;
  onAskUserComplete?: () => void;
}

function logMessageRender(label: string, details: Record<string, unknown>) {
  logChatRenderDebug(
    label,
    details,
    String(details.messageId ?? details.id ?? '')
  );
}

const Message: React.FC<MessageProps> = observer((props) => {
  logMessageRender('message-router-render', {
    messageId: props.message.id,
    role: props.message.role,
    status: props.message.status,
  });

  const { role } = props.message;
  const incomingSender = hivemindIncomingSenderOf(props.message);
  if (incomingSender) {
    const className = props.isCompact ? 'px-1' : 'px-7';
    return (
      <HivemindIncomingMessage
        threadId={props.threadId}
        message={props.message}
        messageIndex={props.messageIndex}
        sender={incomingSender}
        className={className}
      />
    );
  }
  if (role === 'assistant') {
    return <AssistantMessage {...props} />;
  }

  return <UserMessage {...props} />;
});

export default Message;

const UserMessage = observer(
  ({
    threadId,
    message,
    messageIndex,
    isCompact,
    allowEdit = false,
  }: MessageProps) => {
    const { actionCards } = useStores();
    logMessageRender('user-message-render', {
      bodyHash: `${message.body.length}:${message.body.slice(0, 24)}:${message.body.slice(-24)}`,
      bodyLength: message.body.length,
      isCompact,
      isExpanded: message.isExpanded,
      messageId: message.id,
      messageIndex,
      needsTruncation: message.needsTruncation,
      quoteCount: message.quotes.length,
      threadId,
    });

    const containerRef = useRef<ComponentRef<'div'>>(null);
    const { quotes, body } = message;
    const text = body;
    const matchRoute = useMatchRoute();
    const isSharedChat = !!matchRoute({ to: '/shared-chat/$id' });
    const displayText = useAppMentionLinks(
      usePersonMentionLinks(text, {
        enabled: !isCompact && !isSharedChat,
      }),
      {
        enabled: !isSharedChat,
      }
    );
    const messageEdit = useUserMessageEdit(threadId, message, allowEdit);

    const { truncateElementRef, toggleExpand } = useTruncate({
      maxLines: 'auto',
      initiallyExpanded: message.isExpanded,
      needsTruncation: message.needsTruncation,
      onExpand(isExpanded) {
        message.setIsExpanded(isExpanded);
      },
      onTruncationChange(needsTruncation) {
        message.setNeedsTruncation(needsTruncation);
      },
    });

    return (
      <div
        data-message-index={messageIndex}
        data-message-id={message.id}
        data-role="user"
        id={`message-${messageIndex}`}
        className="relative group flex flex-col gap-3 text-foreground"
      >
        {/* Portal target for StreamingLoader — position is carefully calibrated, verify changes visually */}
        <div
          id={loaderPortalId(message.id)}
          className={classList(
            'absolute',
            'left-0',
            '-bottom-7',
            'size-6',
            'pointer-events-none'
          )}
        />

        <Attachments
          fileIds={message.fileIds}
          isAssistant={false}
          key={message.id}
        />

        {message.askUserAnswers &&
          !findAskUserCard(
            actionCards.getByChatId(threadId),
            message.askUserAnswers.request_id
          ) && (
            <div className="mb-2 flex w-full justify-end">
              <div className="max-w-[80%]">
                <AskUserAnswersSummary answers={message.askUserAnswers} />
              </div>
            </div>
          )}

        {messageEdit.isEditing && (
          <UserMessageEditor
            initialBody={text}
            quotes={quotes}
            isCompact={isCompact}
            isSendDisabled={!messageEdit.canEdit}
            onCancel={messageEdit.cancelEditing}
            onSubmit={messageEdit.submitEdit}
          />
        )}

        {!messageEdit.isEditing && (text || quotes.length > 0) && (
          <div className="flex justify-end w-full">
            <div
              data-truncated={message.needsTruncation}
              className={classList(
                'relative',
                isCompact ? 'bg-grayA-3' : 'bg-surface-hover',
                'focus:outline-none',
                'text-foreground',
                'px-3.5 py-1.5',
                'rounded-xl',
                'max-w-[80%]',
                'smoothing-default',
                'break-words',
                'data-[truncated=true]:pr-7',
                {
                  'motion-opacity-in-0 motion-delay-100': !isContentOldEnough(
                    message.createdAt
                  ),
                }
              )}
              ref={containerRef}
              // Hover focuses the bubble so Enter expands a long message. Tab skips it, since the
              // Expand and Show more buttons and the message actions are reachable on their own
              tabIndex={-1}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (message.needsTruncation) toggleExpand();
                }
              }}
              onMouseEnter={() => {
                afterFrames(3, () => {
                  if (containerRef.current)
                    containerRef.current.focus({ preventScroll: true });
                });
              }}
              onMouseLeave={() => {
                afterFrames(3, () => {
                  if (containerRef.current) containerRef.current.blur();
                });
              }}
            >
              <div ref={truncateElementRef} data-allow-selection>
                {quotes.length > 0 && (
                  <MessageQuotes
                    quotes={quotes}
                    className={text ? 'mb-2' : undefined}
                  />
                )}
                <Markdown
                  id={`thread-${threadId}-user-message-${message.id}`}
                  content={displayText}
                  chatId={threadId}
                  actionCardLinks="chip"
                />
              </div>
              {message.needsTruncation && (
                <button
                  type="button"
                  className="text-foreground-muted hover:text-foreground-subtle mt-2"
                  onClick={toggleExpand}
                >
                  Show {message.isExpanded ? 'less' : 'more'}
                </button>
              )}
              {message.needsTruncation && (
                <div
                  className={cn(
                    'absolute top-1.5 right-1.5',
                    'origin-center',
                    'transition-transform duration-200',
                    message.isExpanded && 'rotate-180'
                  )}
                >
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={toggleExpand}
                    className={cn(
                      'rounded-full',
                      'text-foreground-subtle hover:text-foreground',
                      'hover:bg-transparent',
                      'transition-none'
                    )}
                    icon={Icons.chevronDown}
                    aria-expanded={message.isExpanded}
                    aria-label={
                      message.isExpanded ? 'Collapse text' : 'Expand text'
                    }
                    tooltip={
                      message.isExpanded ? 'Collapse text' : 'Expand text'
                    }
                    tooltipOptions={{
                      hotkey: ['enter'],
                      desktop: !inElectron(),
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {message.displayContent && (
          <XStack className="items-center gap-2 justify-end w-full h-[18px] mb-4">
            {!messageEdit.isEditing && (
              <>
                <XStack
                  className={classList(
                    'items-center gap-2',
                    'opacity-0 touch:opacity-100 transition-opacity',
                    'group-hover:opacity-100 group-focus-within:opacity-100'
                  )}
                >
                  {message.needsTruncation && message.isExpanded && (
                    <IconButton
                      size="xs"
                      icon={Icons.chevronUp}
                      onClickCapture={toggleExpand}
                      tooltip="Collapse text"
                      tooltipOptions={{
                        hotkey: ['enter'],
                        desktop: !inElectron(),
                      }}
                    />
                  )}
                  {messageEdit.canEdit && text && (
                    <IconButton
                      size="xs"
                      icon={Icons.edit}
                      aria-label="Edit message"
                      tooltip="Edit message"
                      onClick={messageEdit.startEditing}
                    />
                  )}
                  {text && (
                    <CopyButton
                      text={text}
                      size="xs"
                      copyText="Copy text"
                      onCopy={() => {
                        trackEvent('MESSAGE_USER_COPY_CLICKED', {
                          chatId: threadId,
                          messageIndex: messageIndex,
                        });
                      }}
                    />
                  )}
                </XStack>
                <MessageVersionSwitcher
                  versionIds={message.versionIds}
                  shownVersionId={message.id}
                  disabled={!messageEdit.canSwitchVersion}
                  onShowVersion={messageEdit.showVersion}
                />
              </>
            )}
          </XStack>
        )}
      </div>
    );
  }
);

interface HistoricalAssistantMessageContentProps {
  content: string;
  isCompact: boolean;
  messageId: string;
  onAskSelection: (selection: { text: string }) => void;
  onCopyableEdit: (segmentIndex: number, newText: string) => void;
  threadId: string;
}

const HistoricalAssistantMessageContent = React.memo(
  function HistoricalAssistantMessageContent({
    content,
    isCompact,
    messageId,
    onAskSelection,
    onCopyableEdit,
    threadId,
  }: HistoricalAssistantMessageContentProps) {
    const key = `thread-${threadId}-assistant-message-${messageId}`;

    logMessageRender('historical-message-content-render', {
      contentHash: `${content.length}:${content.slice(0, 24)}:${content.slice(-24)}`,
      contentLength: content.length,
      isCompact,
      messageId,
      threadId,
    });

    return (
      <YStack
        className={cn('text-foreground w-full gap-1', !content && 'hidden')}
        data-allow-selection
      >
        <Markdown
          id={key}
          content={content || ' '}
          enableCopyableBlocks
          copyableBlocksEditable
          isCompact={isCompact}
          onCopyableEdit={onCopyableEdit}
          onAskSelection={onAskSelection}
          chatId={threadId}
        />
      </YStack>
    );
  }
);

const AssistantMessage = observer(
  ({
    threadId,
    message,
    messageIndex,
    isCompact = false,
    isLastAssistantMessage = false,
    isNewestResponse = false,
    isLive = false,
    onAskUserComplete,
  }: MessageProps) => {
    const { messages: messagesStore, actionCards } = useStores();
    const text = message.displayContent;
    const liveAskUserRequest = messagesStore.getLiveAskUserRequest(threadId);
    const askUserRequest =
      onAskUserComplete &&
      isNewestResponse &&
      liveAskUserRequest &&
      !findAskUserCard(
        actionCards.getByChatId(threadId),
        liveAskUserRequest.request_id
      )
        ? liveAskUserRequest
        : null;
    const hasFiles = message.fileIds?.length > 0;
    const pendingSandboxFiles = usePendingSandboxFiles(
      threadId,
      message.id,
      isLastAssistantMessage
    );
    const [isAnimationComplete, setIsAnimationComplete] = useState(false);
    const {
      handleCopyableEdit,
      handleAskSelection,
      handleImageLoaded,
      handleImageError,
      handleImageViewed,
      handleImageDownloaded,
    } = useAssistantMessageCallbacks(threadId, message);
    // Keep controls hidden until streaming ends so Read Aloud uses final text
    const showControls =
      message.status === ChatStatus.Done ||
      message.status === ChatStatus.Stopped ||
      message.status === ChatStatus.Errored;

    return (
      <div
        data-message-index={messageIndex}
        data-message-id={message.id}
        data-role="assistant"
        id={`message-${message.id}`}
        className={cn('relative group flex flex-col gap-3', {
          'px-1': isCompact,
          'px-7': !isCompact,
        })}
      >
        {/* Portal target for StreamingLoader — position is carefully calibrated, verify changes visually */}
        <div
          id={loaderPortalId(message.id)}
          className={classList(
            'absolute',
            '-left-1',
            'top-0.5',
            'size-6',
            'pointer-events-none'
          )}
        />
        {isLive ? (
          <MessageContent
            text={text}
            threadId={threadId}
            message={message}
            isCompact={isCompact}
            onAnimationComplete={() => setIsAnimationComplete(true)}
          />
        ) : (
          <HistoricalAssistantMessageContent
            content={text}
            threadId={threadId}
            messageId={message.id}
            isCompact={isCompact}
            onCopyableEdit={handleCopyableEdit}
            onAskSelection={handleAskSelection}
          />
        )}
        {askUserRequest && (
          <div className="w-full max-w-[85%]">
            <AskUserPicker
              request={askUserRequest}
              onComplete={onAskUserComplete}
            />
          </div>
        )}
        {message.fileIds.length > 0 ? (
          <Attachments
            fileIds={message.fileIds}
            isAssistant={true}
            pendingFiles={pendingSandboxFiles}
            onImageLoaded={handleImageLoaded}
            onImageError={handleImageError}
            onImageViewed={handleImageViewed}
            onImageDownloaded={handleImageDownloaded}
            key={message.id}
          />
        ) : (
          <PendingAssistantFiles files={pendingSandboxFiles} />
        )}

        {message.status === ChatStatus.Stopped && (text || hasFiles) ? (
          <span className="text-xs text-foreground-subtle">Interrupted</span>
        ) : null}

        <MessageActionCards
          threadId={threadId}
          assistantMessageId={message.id}
          isTurnTail={!!isLastAssistantMessage}
          onAskUserComplete={onAskUserComplete}
        />

        {(text || hasFiles) && showControls && isLastAssistantMessage && (
          <XStack
            className={classList(
              'py-1',
              'flex gap-1 items-center',
              'mb-4',
              'transition-opacity',
              isNewestResponse
                ? {
                    'motion-opacity-in-0 motion-delay-150': isAnimationComplete,
                  }
                : [
                    'opacity-0',
                    'touch:opacity-100',
                    'group-hover:opacity-100 group-focus-within:opacity-100',
                    'has-[[data-read-aloud-playing]]:opacity-100',
                  ]
            )}
          >
            {text && (
              <CopyButton
                text={text}
                copyText="Copy text"
                className="flex"
                size="xs"
                data-testid="copy-response-button"
                onCopy={() => {
                  trackEvent('MESSAGE_LB_COPY_CLICKED', {
                    chatId: threadId,
                    messageIndex: messageIndex,
                  });
                }}
              />
            )}
            {text && (
              <CopyAsImageButton
                text={text}
                threadId={threadId}
                messageId={message.id}
                messageIndex={messageIndex}
              />
            )}
            {text && canUseSpeechFeatures() && (
              <SpeakResponseButton
                text={text}
                threadId={threadId}
                messageId={message.id}
                messageIndex={messageIndex}
              />
            )}

            <VoteButton
              direction={VoteDataDirection.UP}
              threadId={threadId}
              messageId={message.id}
              isCompact={isCompact}
            />
            <VoteButton
              direction={VoteDataDirection.DOWN}
              threadId={threadId}
              messageId={message.id}
              isCompact={isCompact}
            />
            <MessageSources message={message} className="ml-2" />
          </XStack>
        )}
      </div>
    );
  }
);

/**
 * Determines if a message is old enough to skip initial animation.
 * Messages created before the user opened the chat should display
 * their existing content immediately rather than animating from empty.
 *
 * @param createdAt - The timestamp when the message was created
 * @param thresholdMs - Age threshold in milliseconds (default 500ms)
 * @returns true if the message is older than the threshold
 */
function isContentOldEnough(createdAt: Date | undefined, thresholdMs = 500) {
  if (!createdAt) return false;
  return Date.now() - createdAt.getTime() > thresholdMs;
}

interface MessageContentProps {
  text: string;
  threadId: string;
  message: TMessage;
  isCompact?: boolean;
  onAnimationComplete?(): void;
}

export const MessageContent: React.FC<MessageContentProps> = observer(
  ({ text, threadId, message, isCompact = false, onAnimationComplete }) => {
    logMessageRender('message-content-render', {
      contentHash: `${text.length}:${text.slice(0, 24)}:${text.slice(-24)}`,
      contentLength: text.length,
      createdAt: message.createdAt?.toISOString?.(),
      isCompact,
      messageId: message.id,
      status: message.status,
      threadId,
    });

    /**
     * Animation State Management:
     *
     * This component handles three display scenarios for message content:
     * 1. Streaming messages: Display with typewriter animation as content arrives
     * 2. Old streaming messages: Show existing content immediately, then animate new content
     * 3. Static messages: Display all content immediately without animation
     *
     * The animation state is coordinated between this component and the thread's
     * animation tracker to ensure only one message animates at a time.
     */

    const { handleCopyableEdit, handleAskSelection } =
      useAssistantMessageCallbacks(threadId, message);

    const { claim, release } = useAnimationSlot(threadId, message.id);

    /** For messages older than 500ms, show existing content immediately */
    const resume = isContentOldEnough(message.createdAt, 500);

    const shouldAnimateOnInitialRender =
      message.status === ChatStatus.Streaming ||
      (message.status === ChatStatus.Done && !resume);

    // Only hydrate initial content for historical messages that should not animate
    const adoptInitialContent = resume && !shouldAnimateOnInitialRender;

    /** Controls whether content should animate (typewriter effect) */
    const animate = useRef(shouldAnimateOnInitialRender);

    const key = `thread-${threadId}-assistant-message-${message.id}`;

    // Backend token batching can deliver content in ~1s bursts
    // Keep a per-message speed controller so each burst drains smoothly
    const isBackendDone = message.status === ChatStatus.Done;
    logChatRenderDebug(
      'message-content-render-state',
      {
        adoptInitialContent,
        animateCurrent: animate.current,
        isBackendDone,
        messageId: message.id,
        resume,
        shouldAnimateOnInitialRender,
      },
      message.id
    );

    const batchAwareSpeed = useMemo(
      () =>
        createBatchAwareSpeed({
          batchWindowSec: 1.3,
          minWindowMs: 1100,
          maxWindowMs: 2600,
          emaAlpha: 0.2,
          jitterMarginMs: 140,
          minCharactersPerFrame: 0.08,
          maxCharactersPerFrame: 18,
          reserveCharacters: 40,
          resetGapMs: 3000,
          fastFinish: isBackendDone,
          fastFinishWindowSec: 0.24,
        }),
      [isBackendDone]
    );

    // Sync animation state with message streaming status
    // This ensures animation starts when a message begins streaming
    useEffect(() => {
      if (message.status === ChatStatus.Streaming) {
        animate.current = true;
      }
    }, [message]);

    // Handle manual stop events from the Composer
    // When user clicks stop button, Composer dispatches this event to immediately
    // halt the typewriter animation, preventing it from continuing after the
    // message stream has been interrupted
    useEffect(() => {
      if (!animate.current) return;

      const handle = ({
        messageId: eventMessageId,
      }: {
        messageId?: string;
        threadId: string;
      }) => {
        if (eventMessageId !== message.id) return;
        animate.current = false;
      };

      const streamingMessageWillStopSubscription = globalEventBus.on(
        EventNames.CHAT_STREAMING_MESSAGE_WILL_STOP,
        ({ data }) => handle(data)
      );

      return () => streamingMessageWillStopSubscription.unsubscribe();
    }, [message]);

    if (isBackendDone && adoptInitialContent) {
      return (
        <YStack
          className={cn('text-foreground w-full gap-1', !text && 'hidden')}
          data-allow-selection
        >
          <Markdown
            id={key}
            content={text || ' '}
            enableCopyableBlocks
            copyableBlocksEditable
            isCompact={isCompact}
            onCopyableEdit={handleCopyableEdit}
            onAskSelection={handleAskSelection}
            chatId={threadId}
          />
        </YStack>
      );
    }

    return (
      <YStack
        className={cn('text-foreground w-full gap-1', !text && 'hidden')}
        data-allow-selection
      >
        <Smoothdown
          key={key}
          id={key}
          enableCopyableBlocks
          chatId={threadId}
          telemetryThreadId={threadId}
          telemetryMessageId={message.id}
          copyableBlocksEditable={message.status !== ChatStatus.Streaming}
          isCompact={isCompact}
          // Enable typewriter animation for actively streaming messages
          smooth={animate.current}
          content={text || ' '} // A hacky way to make sure modality chips are shown even for empty messages. Without
          // this, the Smoothdown component never dispatches the STREAMING_MESSAGE_DID_STOP event, and isStreaming
          // never gets set to false. Modality chips currently stay hidden until it is set to false. The fact that
          // isStreaming wasn't getting set to false may have been responsible for some other UI quirks.
          //
          adoptInitialContent={adoptInitialContent}
          variableCharactersPerFrame={batchAwareSpeed}
          onCopyableEdit={handleCopyableEdit}
          onAskSelection={handleAskSelection}
          flushOnDisable
          onAnimationStart={claim}
          onAnimationComplete={(flushed) => {
            release();
            if (!flushed) {
              globalEventBus.emit(EventNames.CHAT_STREAMING_MESSAGE_DID_STOP, {
                data: {
                  threadId,
                  messageId: message.id,
                },
              });
              onAnimationComplete?.();
            }
          }}
        />
      </YStack>
    );
  }
);

interface VoteButtonProps {
  direction: VoteDataDirectionType;
  threadId: string;
  messageId: string;
  isCompact?: boolean;
}

function VoteButton({
  direction,
  threadId,
  messageId,
  isCompact = false,
}: VoteButtonProps) {
  const { mutate: vote } = useVote({ chat_id: threadId });

  const icon = useMemo(
    () =>
      direction === VoteDataDirection.UP ? Icons.thumbsUp : Icons.thumbsDown,
    [direction]
  );

  const onVote = useCallback(
    (reasoning: string, includeChatHistory: boolean) => {
      vote({
        id: messageId,
        direction,
        reasoning,
        include_chat_history: includeChatHistory,
      });

      trackEvent('MESSAGE_RATED', {
        chat_id: threadId,
        message_id: messageId,
        direction,
        ...(isCompact ? { source: 'hummingbird' } : {}),
      });

      submitSurvey('chat message feedback', [
        threadId,
        messageId,
        direction === VoteDataDirection.UP ? 'Up' : 'Down',
        reasoning,
      ]).catch(captureException);
    },
    [direction, messageId, threadId, vote, isCompact]
  );

  const props = {
    tooltipText:
      direction === VoteDataDirection.UP ? 'Great job!' : 'Not helpful',
    onClick: onVote,
    icon,
    direction,
  };

  return isCompact ? (
    <CompactVoteButton {...props} />
  ) : (
    <NormalVoteButton {...props} />
  );
}

interface VoteActionButtonProps {
  tooltipText: string;
  onClick: (reasoning: string, includeChatHistory: boolean) => void;
  icon: LBIcon;
  direction: VoteDataDirectionType;
}

const CompactVoteButton = ({
  tooltipText,
  onClick,
  icon,
}: VoteActionButtonProps) => {
  return (
    <IconButton
      tooltip={tooltipText}
      size="xs"
      icon={icon}
      onClick={() => {
        onClick('', false);
        toast.info('Thank you for your feedback!', {
          position: 'top-center',
          className: 'mt-20',
        });
      }}
    />
  );
};

const NormalVoteButton = ({
  tooltipText,
  onClick,
  icon,
  direction,
}: VoteActionButtonProps) => {
  const matchRoute = useMatchRoute();
  const isChatView =
    matchRoute({ from: '/chats/$chatId' }) ||
    matchRoute({ from: '/routines/$routineId' });

  const handleVote = useCallback(() => {
    ModalsManager.show(VoteMessageModal, {
      textInputHint:
        direction === VoteDataDirection.UP
          ? 'What do you think went well?'
          : 'What do you think could be improved?',
      onVote: onClick,
      direction,
    });
  }, [onClick, direction]);

  if (!isChatView) return null;

  return (
    <IconButton
      tooltip={tooltipText}
      size="xs"
      icon={icon}
      onClick={handleVote}
    />
  );
};
